import { useEffect, useState } from 'react';

import { useAuthInfo } from '@propelauth/react';

import { APIBaseChronos } from '@/api/hosts';

export enum RelativitySyncEventType {
  Progress = 'relativity-sync-progress',
  Complete = 'relativity-sync-complete',
  Error = 'relativity-sync-error',
}

export interface SyncProgressEvent {
  type: RelativitySyncEventType.Progress;
  totalFiles: number;
  processedFiles: number;
  matched: number;
  unmatched: number;
  failed: number;
  status: 'preparing' | 'processing';
  preparingProgress?: number;
  preparingMessage?: string;
  totalChunks?: number;
}

export interface SyncCompleteEvent {
  type: RelativitySyncEventType.Complete;
  totalFiles: number;
  matched: number;
  unmatched: number;
  failed: number;
  duration: number;
  droppedFields?: Array<{ fieldName: string; documentCount: number }>;
  auditTrailId?: string;
}

export interface SyncErrorEvent {
  type: RelativitySyncEventType.Error;
  error: string;
}

export type SyncEvent = SyncProgressEvent | SyncCompleteEvent | SyncErrorEvent;

interface UseRelativitySyncProgressReturn {
  progress: SyncProgressEvent | null;
  complete: SyncCompleteEvent | null;
  error: string | null;
  isConnected: boolean;
}

const useRelativitySyncProgress = (jobId: string | undefined): UseRelativitySyncProgressReturn => {
  const { accessToken } = useAuthInfo();
  const [progress, setProgress] = useState<SyncProgressEvent | null>(null);
  const [complete, setComplete] = useState<SyncCompleteEvent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!jobId || !accessToken) {
      return;
    }

    let intervalId: NodeJS.Timeout | null = null;

    const fetchProgress = async () => {
      try {
        const response = await fetch(`${APIBaseChronos}/api/relativity/sync/${jobId}/status`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch progress: ${response.status}`);
        }

        const data = await response.json();

        if (data.status === 'complete') {
          setComplete({
            type: RelativitySyncEventType.Complete,
            totalFiles: data.total,
            matched: data.matched,
            unmatched: data.unmatched,
            failed: data.failed,
            duration: 0,
            auditTrailId: data.auditTrailId,
          });
          if (intervalId) {
            clearInterval(intervalId);
          }
        } else if (data.status === 'failed') {
          setError('Sync job failed');
          if (intervalId) {
            clearInterval(intervalId);
          }
        } else {
          setProgress({
            type: RelativitySyncEventType.Progress,
            totalFiles: data.total,
            processedFiles: data.processed,
            matched: data.matched,
            unmatched: data.unmatched,
            failed: data.failed,
            status: 'processing',
          });
        }

        setIsConnected(true);
      } catch (err) {
        console.error('Failed to fetch sync progress:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch progress');
      }
    };

    // Initial fetch
    fetchProgress();

    // Poll every second
    intervalId = setInterval(fetchProgress, 1000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [jobId, accessToken]);

  return {
    progress,
    complete,
    error,
    isConnected,
  };
};

export default useRelativitySyncProgress;
